import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { useSearchParams } from 'react-router-dom';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import { Plus, Inbox, Search, X, Filter as FilterIcon, ChevronDown } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import BulkBar from 'shared/components/tables/BulkBar';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';
import TableFooter from 'shared/components/tables/TableFooter';
import Icon from 'shared/components/Icon';
import {
  activityToolbarButtonClass, activitySearchWrapperClass, activitySearchInputClass, dataTableFocusRing,
} from 'features/activity/styles/dataTableStyles';
import EvalsTable, { LEVEL_ICON } from 'features/evals3/components/EvalsTable';
import NewEvalDialog from 'features/evals3/components/NewEvalDialog';
import { TARGETS } from 'features/evals3/templates';
import { openAsk } from 'shared/askDraft';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { listLineagesPage, patchLineage, deleteLineage, getQueueCounts, listProposals } from 'features/evals3/api/evals3Api';
import Eval3QueuePage from 'features/evals3/pages/Eval3QueuePage';
import Eval3ProposalsPage from 'features/evals3/pages/Eval3ProposalsPage';
import { viewFromParams, paramsForView } from 'features/evals3/pages/evals3View';
import useTeamId from 'features/evals3/useTeamId';
import Eval3CreatePage from 'features/evals3/pages/Eval3CreatePage';
import Eval3EditorPage from 'features/evals3/pages/Eval3EditorPage';
import Eval3ResultsPage from 'features/evals3/pages/Eval3ResultsPage';
import { PageLoader } from 'shared/components/ui/BrandLoader';

/**
 * The new evals: the list, and the screens behind it.
 *
 * A NEW component tree, deliberately. Nothing here imports from
 * features/evals: the current evals are going to be deleted outright, and
 * that has to be a file delete rather than an untangling. Where something on
 * the old side was good, it is duplicated, not shared.
 *
 *   list     → the team's evals, a page at a time (50), searched on the server:
 *              name, target, agent, version, enabled
 *   create   → name it, say what it scores and how it decides; opens the editor
 *   editor   → the graph, each step's settings, versions, test on a trace
 *   results  → verdicts, and the journal behind each one
 */

const PAGE = 50;
// The menu item a dataset's "Add" menu uses, so the toolbar menus match.
const menuItemClass = `flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover ${dataTableFocusRing}`;

function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{label}</span>
      {children}
    </div>
  );
}

export default function Evals3Page() {
  const teamId = useTeamId();
  const { confirm } = useAppDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = useMemo(() => viewFromParams(searchParams), [searchParams]);
  const setView = useCallback((next) => setSearchParams(() => paramsForView(next)), [setSearchParams]);
  const ownsCrumbs = view.name !== 'list';
  useBreadcrumb(ownsCrumbs ? [] : [{ label: 'Evals' }]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({ agent: '', level: '', scored: '' });
  const [filterOpen, setFilterOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState('');   // the query, once typing pauses
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const [choosing, setChoosing] = useState(false); // the "New eval" pop-up: a template, from scratch, or Ask

  // Escape closes the toolbar's pop-overs, as every other menu in the app does.
  useEffect(() => {
    if (!filterOpen && !actionOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setFilterOpen(false); setActionOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [filterOpen, actionOpen]);

  useEffect(() => {
    const t = setTimeout(() => { setSearched(query.trim()); }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const page = await listLineagesPage(teamId, { limit: PAGE, q: searched });
      setRows(page.lineages);
      setTotal(page.total);
      setError(null);
    }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [teamId, searched]);
  useEffect(() => { refresh(); }, [refresh]);

  const loadMore = useCallback(async () => {
    if (!teamId || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listLineagesPage(teamId, { limit: PAGE, offset: rows.length, q: searched });
      setRows((cur) => { const seen = new Set(cur.map((r) => r.id)); return [...cur, ...page.lineages.filter((r) => !seen.has(r.id))]; });
      setTotal(page.total);
    }
    catch (e) { setError(e?.response?.data?.error || e.message); }
    finally { setLoadingMore(false); }
  }, [teamId, loadingMore, rows.length, searched]);

  // The page loaded so far, narrowed. The list endpoint filters on search only.
  const agents = useMemo(() => [...new Set(rows.map((r) => r.agent_name).filter(Boolean))].sort(), [rows]);
  const shownRows = useMemo(() => {
    const since = { '1d': 864e5, '7d': 7 * 864e5, '30d': 30 * 864e5 }[filters.scored];
    return rows.filter((r) => {
      if (filters.agent && r.agent_name !== filters.agent) return false;
      if (filters.level && r.target !== filters.level) return false;
      if (filters.scored === 'never') return !r.last_scored_at;
      if (since) return r.last_scored_at && Date.now() - new Date(r.last_scored_at).getTime() <= since;
      return true;
    });
  }, [rows, filters]);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // A reload or a filter can drop rows the selection still holds.
  useEffect(() => { setPicked((cur) => (cur.size ? new Set([...cur].filter((id) => shownRows.some((r) => r.id === id))) : cur)); }, [shownRows]);
  const chosen = shownRows.filter((r) => picked.has(r.id));
  const toggleOne = (id) => setPicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  /** One call per eval, only where the state changes; what failed is said, what worked is kept. */
  const setEnabled = async (enabled) => {
    const targets = chosen.filter((r) => Boolean(r.enabled) !== enabled);
    setBusy(true);
    const res = await Promise.allSettled(targets.map((r) => patchLineage(teamId, r.id, { enabled })));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await refresh();   // a good reload clears the error, so say what failed after it
    if (failed.length) setError(failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed');
    setBusy(false);
  };
  /** Asks first; then one call per eval, the failures said after the reload. */
  const removeChosen = async () => {
    if (!chosen.length) return;
    const many = chosen.length > 1;
    const ok = await confirm(
      `${many ? `${chosen.length} evals` : `"${chosen[0].name}"`} and the results will be removed. Alerts that watch ${many ? 'them' : 'it'} are removed too.`,
      { title: many ? 'Delete evals' : 'Delete eval', confirmLabel: 'Delete', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    const res = await Promise.allSettled(chosen.map((r) => deleteLineage(teamId, r.id)));
    const failed = res.filter((x) => x.status === 'rejected');
    setPicked(new Set());
    await refresh();
    if (failed.length) setError(failed[0].reason?.response?.data?.error || failed[0].reason?.message || 'Something failed');
    setBusy(false);
  };
  const activeFilters = Object.values(filters).filter(Boolean).length;

  // A growing queue has to be visible from the list (O5).
  const [queueTotal, setQueueTotal] = useState(0);
  useEffect(() => {
    if (!teamId || view.name !== 'list') return;
    getQueueCounts(teamId).then((c) => setQueueTotal(c.total)).catch(() => setQueueTotal(0));
  }, [teamId, view.name]);

  // Proposed changes waiting for a decision.
  const [proposalsOpen, setProposalsOpen] = useState(0);
  useEffect(() => {
    if (!teamId || view.name !== 'list') return;
    Promise.resolve().then(() => listProposals(teamId, { status: 'open' }))
      .then((r) => setProposalsOpen(r?.counts?.open || 0)).catch(() => setProposalsOpen(0));
  }, [teamId, view.name]);


  if (view.name === 'create') {
    return <Eval3CreatePage key={view.template || 'scratch'} teamId={teamId} templateId={view.template || null}
      onScratch={() => setView({ name: 'create' })}
      onCancel={() => setView({ name: 'list' })} onCreated={(lineage) => { refresh(); setView({ name: 'editor', id: lineage.id }); }} />;
  }
  if (view.name === 'editor') {
    return <Eval3EditorPage key={`${view.id}:${view.previewVersion ?? ''}`} teamId={teamId} lineageId={view.id} initialPreviewVersion={view.previewVersion ?? null} onBack={() => setView({ name: 'list' })} onResults={() => setView({ name: 'results', id: view.id })} onChanged={refresh} />;
  }
  if (view.name === 'queue') {
    return <Eval3QueuePage key={view.id || 'all'} teamId={teamId} lineageId={view.id || null} onBack={() => setView({ name: 'list' })} onLineage={(id) => setView(id ? { name: 'queue', id } : { name: 'queue' })} />;
  }
  if (view.name === 'proposals') {
    return <Eval3ProposalsPage key={view.id || 'all'} teamId={teamId} lineageId={view.id || null} onBack={() => setView({ name: 'list' })} onAll={() => setView({ name: 'proposals' })} onEdit={(id) => setView({ name: 'editor', id })} />;
  }
  if (view.name === 'results') {
    // An eval's home is its results. Edit is a mode stepped into from there.
    return <Eval3ResultsPage key={`${view.id}:${view.runId || ''}`} teamId={teamId} lineageId={view.id} initialRunId={view.runId || null} initialFilter={view.filter || null} onBack={() => setView({ name: 'list' })} onEdit={(opts) => setView({ name: 'editor', id: view.id, previewVersion: opts?.previewVersion ?? null })} onChanged={refresh} />;
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <NewEvalDialog teamId={teamId} open={choosing} onClose={() => setChoosing(false)}
        onPick={(t) => { setChoosing(false); setView({ name: 'create', template: t.id }); }}
        onScratch={() => { setChoosing(false); setView({ name: 'create' }); }}
        onAsk={() => { setChoosing(false); openAsk('Create an eval that '); }} />
      <div className="flex shrink-0 flex-col gap-2 bg-surface px-3 pb-2 pt-3 sm:px-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className={`${activitySearchWrapperClass} w-[240px]`}>
          <Search size={14} className="flex-none text-[color:var(--text-muted)]" aria-hidden />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search evals…" aria-label="Search evals" className={activitySearchInputClass} />
          {query ? (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="flex-none text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"><X className="h-3.5 w-3.5" /></button>
          ) : null}
        </div>
        <div className="relative">
          <button type="button" onClick={() => setFilterOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={filterOpen} className={activityToolbarButtonClass}>
            <FilterIcon size={13} aria-hidden /> Filter
            {activeFilters > 0 && <span className="rounded-full bg-brand-soft px-1.5 text-[10.5px] font-semibold text-brand">{activeFilters}</span>}
          </button>
          {filterOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setFilterOpen(false)} role="presentation" />
              <div role="dialog" aria-label="Filter evals" className="absolute left-0 top-full z-50 mt-1 flex w-64 flex-col gap-3 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-3 shadow-token-lg">
                <FilterField label="Agent">
                  <BrandDropdown compact ariaLabel="Agent" value={filters.agent} onChange={(v) => setFilters((f) => ({ ...f, agent: v }))}
                    options={[{ value: '', label: 'Any agent' }, ...agents.map((a) => ({ value: a, label: a }))]} />
                </FilterField>
                <FilterField label="Level">
                  <div className="flex gap-1">
                    {TARGETS.map((t) => (
                      <button key={t.value} type="button" aria-pressed={filters.level === t.value} onClick={() => setFilters((f) => ({ ...f, level: f.level === t.value ? '' : t.value }))}
                        className={`inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md border text-[11.5px] ${filters.level === t.value ? 'border-[color:var(--trodo-primary)] text-[color:var(--text-primary)]' : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}>
                        <Icon name={LEVEL_ICON[t.value]} size={13} color="currentColor" /> {t.label}
                      </button>
                    ))}
                  </div>
                </FilterField>
                <FilterField label="Last scored">
                  <BrandDropdown compact ariaLabel="Last scored" value={filters.scored} onChange={(v) => setFilters((f) => ({ ...f, scored: v }))}
                    options={[{ value: '', label: 'Any time' }, { value: '1d', label: 'Last 24 hours' }, { value: '7d', label: 'Last 7 days' }, { value: '30d', label: 'Last 30 days' }, { value: 'never', label: 'Never' }]} />
                </FilterField>
                {activeFilters > 0 && (
                  <button type="button" onClick={() => setFilters({ agent: '', level: '', scored: '' })} className="self-start text-[11.5px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Clear</button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="flex-1" />
        <div className="relative">
          <button type="button" onClick={() => setActionOpen((v) => !v)} aria-haspopup="menu" aria-expanded={actionOpen} className={activityToolbarButtonClass}>
            Action
            {(queueTotal + proposalsOpen) > 0 && <span className="rounded-full bg-brand-soft px-1.5 text-[10.5px] font-semibold text-brand">{queueTotal + proposalsOpen}</span>}
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
          {actionOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setActionOpen(false)} role="presentation" />
              <div role="menu" aria-label="Action" className="absolute right-0 top-[calc(100%+4px)] z-50 w-56 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-lg">
                <button type="button" role="menuitem" onClick={() => { setActionOpen(false); setView({ name: 'queue' }); }} aria-label={`Grading queue, ${queueTotal} waiting`}
                  className={menuItemClass}>
                  <Inbox size={13} className="text-[color:var(--text-muted)]" aria-hidden /> <span className="flex-1">Grading queue</span>
                  {queueTotal > 0 && <span className="rounded-full bg-warning-soft px-1.5 text-[10.5px] font-semibold text-warning">{queueTotal}</span>}
                </button>
                <button type="button" role="menuitem" onClick={() => { setActionOpen(false); setView({ name: 'proposals' }); }} aria-label={`Proposed changes, ${proposalsOpen} open`}
                  className={menuItemClass}>
                  <AskIcon size={13} className="text-[color:var(--brand-primary)]" /> <span className="flex-1">Proposed changes</span>
                  {proposalsOpen > 0 && <span className="rounded-full bg-brand-soft px-1.5 text-[10.5px] font-semibold text-brand">{proposalsOpen}</span>}
                </button>
              </div>
            </>
          )}
        </div>
        {canWrite && (
          <button type="button" onClick={() => setChoosing(true)} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover">
            <Plus size={13} /> New eval
          </button>
        )}
      </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col px-4 pb-8 pt-3">
          {error && <div className="mb-3 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-3 text-[12px] text-danger">{error}</div>}
          {loading && !rows.length && <PageLoader label="Loading evals" />}
          {!loading && !rows.length && !error && searched && (
            <div className="flex h-32 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              No eval matches “{searched}”.
            </div>
          )}
          {/* Where the table would be: what is there, which is nothing. New
              eval is in the toolbar above, on this page and on every other —
              a second copy here made the empty state look like a dialog and
              moved the button a reader had already found. Same shape as the
              no-match state above it. */}
          {!loading && !rows.length && !error && !searched && (
            <div className="flex h-32 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-6 text-center text-[12px] text-[color:var(--text-muted)]">
              No evals yet.
            </div>
          )}
          {rows.length > 0 && (
            <>
              <EvalsTable rows={shownRows} query={searched} onOpen={(r) => setView({ name: 'results', id: r.id })}
                picked={canWrite ? picked : undefined} onToggle={canWrite ? toggleOne : undefined} onToggleAll={(on) => setPicked(on ? new Set(shownRows.map((r) => r.id)) : new Set())} />
              <TableFooter shown={shownRows.length} total={activeFilters ? null : total} hasMore={rows.length < total} loading={loadingMore} onLoadMore={loadMore}
                note={activeFilters ? `${shownRows.length.toLocaleString()} of ${rows.length.toLocaleString()} loaded match` : null} />
            </>
          )}
        </div>
      </div>
      <BulkBar count={canWrite ? picked.size : 0} label="Selected evals" onClear={() => setPicked(new Set())} actions={[
        { label: 'Enable', disabled: busy || !chosen.some((r) => !r.enabled), onClick: () => setEnabled(true) },
        { label: 'Disable', disabled: busy || !chosen.some((r) => r.enabled), onClick: () => setEnabled(false) },
        { label: 'Delete', disabled: busy, onClick: removeChosen, danger: true },
      ]} />
    </main>
  );
}
